import React from 'react';
import { Classable, HasChildren } from '@shapeable/types';
import styled, { css } from 'styled-components';
import { breakpoints, theme } from '@shapeable/theme';
import { Topic } from '@shapeable/gesda-types';
import { Content, ContentNode, ImageAsset, MarkdownContent, SectionTitle } from '@shapeable/ui';

// -------- Types -------->

export type TopicAnticipatoryImpactLayoutProps = Classable & HasChildren & {
  entity?: Topic;
  title?: string;
}

export const TopicAnticipatoryImpactLayoutDefaultProps: Omit<TopicAnticipatoryImpactLayoutProps, 'entity'> = {
  title: 'Anticipatory Impact:',
};

// -------- Child Component Props -------->

type ContainerProps = {
  hasImage?: boolean;
}

type ChartProps = {
  hasText?: boolean;
}

type ImageProps = {
  hasText?: boolean;
}

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(8)};
    box-sizing: border-box;
  `,
  tablet: css`
    flex-direction: ${({ hasImage }: ContainerProps ) => hasImage ? 'row' : 'column'};
    align-items: flex-start;
  `,
});

const BodyStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
  `,
});

const HeadingStyles = breakpoints({
  base: css`
    font-weight: 400;
  `,
});

const ContentStyles = breakpoints({
  base: css`
    font-weight: 300;
  `,
});

const ImageStyles = breakpoints({
  base: css`
    align-self: center;
    width: 80%;
    height: auto;
    flex-shrink: 0;

  `,
  tablet: css`
    align-self: ${({ hasText }: ChartProps ) => hasText ? 'center' : 'flex-start'};
    width: 40%;
  `,
});

const ChartStyles = breakpoints({
  base: css`
    display: flex;
    justify-content: center;
    align-self: center;
    width: 80%;
    flex-shrink: 0;
  `,
  tablet: css`
    align-self: ${({ hasText }: ChartProps ) => hasText ? 'center' : 'flex-start'};
    justify-content: center;
    width: 40%;
  `,
});

// -------- Components -------->

const My = {
    Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Body: styled.div`${BodyStyles}`,
    Heading: styled(SectionTitle)`${HeadingStyles}`,
    Content: styled(ContentNode)`${ContentStyles}`,
    Image: styled(ImageAsset)<ImageProps>`${ImageStyles}`,
    Chart: styled.div<ChartProps>`${ChartStyles}`,
};

export const TopicAnticipatoryImpactLayout: React.FC<TopicAnticipatoryImpactLayoutProps> = (props) => {
  const { className, children, title, entity } = props;
  const { anticipatoryImpact, anticipatoryImpactImage } = entity;

  const hasText = anticipatoryImpact && !!anticipatoryImpact.text;
  const hasImage = anticipatoryImpactImage && anticipatoryImpactImage.image && anticipatoryImpactImage.image.url;
  const hasChildren = React.Children.count(children) > 0;

  return (
    <My.Container className={className} hasImage={!!hasText}>
      <My.Body>
        <My.Heading>{title}</My.Heading>
        {
          hasText && <My.Content entity={anticipatoryImpact} />
        }
      </My.Body>
        {
          hasChildren ? <My.Chart hasText={!!hasText}>{children}</My.Chart> : hasImage && <My.Image entity={anticipatoryImpactImage} hasText={!!hasText} />
        }
    </My.Container>
  )
};

TopicAnticipatoryImpactLayout.defaultProps = TopicAnticipatoryImpactLayoutDefaultProps;